import { BookingDetails } from 'app/data/types/booking';

/** Why a booking can't be cancelled (drives the disabled reason copy). */
export type NotCancelableReason = 'booking_in_the_past' | 'cancellation_period_closed';

export interface BookingCancelable {
  cancelable: boolean;
  reason?: NotCancelableReason;
}

const MS_PER_MIN = 60_000;

/**
 * Whether an ACTIVE booking may be cancelled — ported from resource-booking's
 * `use-is-booking-cancelable.ts`. Uses `start_at`, `cancellation_window_end_minutes`,
 * `allow_late_cancel`, `allow_cancel_during`. (An already-cancelled booking is handled upstream by the
 * status view, not here.)
 */
export const getBookingCancelable = (details: BookingDetails, now: Date = new Date()): BookingCancelable => {
  const startMs = details.start_at ? new Date(details.start_at).getTime() : NaN;
  // Eligibility can't be established without a valid start time → do NOT enable cancel.
  if (Number.isNaN(startMs)) return { cancelable: false };

  const nowMs = now.getTime();
  const windowMin = details.cancellation_window_end_minutes;
  const allowLate = !!details.allow_late_cancel;
  const allowDuring = !!details.allow_cancel_during;

  const isPast = nowMs > startMs;
  const windowExpired = windowMin != null && nowMs > startMs - windowMin * MS_PER_MIN;

  // Single decision path (no duplicated reason/cancelable branches that could drift):
  if (isPast) {
    return allowDuring ? { cancelable: true } : { cancelable: false, reason: 'booking_in_the_past' };
  }
  if (windowExpired) {
    return allowLate ? { cancelable: true } : { cancelable: false, reason: 'cancellation_period_closed' };
  }
  return { cancelable: true };
};
